import { Button, Input } from "@mantine/core";
import { Checkbox, Table, notification } from "antd";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CSS } from "@dnd-kit/utilities";
import type { DragEndEvent } from "@dnd-kit/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { orpcClient } from "@/lib/orpc-client";
import { single, allOrThrow } from "@/lib/orpc-batch";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface DraggableHeaderCellProps
  extends React.HTMLAttributes<HTMLTableCellElement> {
  id?: string;
}

const DraggableHeaderCell: React.FC<DraggableHeaderCellProps> = ({
  id,
  ...restProps
}) =>
  id ? <SortableHeaderCell id={id} {...restProps} /> : <th {...restProps} />;

const SortableHeaderCell: React.FC<
  DraggableHeaderCellProps & { id: string }
> = ({
  id,
  children,
  ...restProps
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    ...restProps.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "move",
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <th
      {...restProps}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {children}
    </th>
  );
};

function SteelBoltBOM() {
  const { t } = useTranslation();
  const [fileUpload, setFileUpload] = useState([]);
  const [designBOM, setDesignBOM] = useState([]);
  const [editRowId, setEditRowId] = useState(null);
  const [editRowData, setEditRowData] = useState([]);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteFileUpload, setDeleteFileUpload] = useState([]);
  const [api, contextHolder] = notification.useNotification();

  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const fileUploadRef = useRef(fileUpload);

  const COLUMN_ORDER_STORAGE_KEY = `steelBoltBOM_columnOrder_${id}`;
  const COLUMN_VISIBILITY_STORAGE_KEY = `steelBoltBOM_columnVisibility_${id}`;

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const getCurrentPageFromURL = () => {
    const page = searchParams.get("page");
    return page ? parseInt(page, 10) : 1;
  };

  const getPageSizeFromURL = () => {
    const size = searchParams.get("pageSize");
    return size ? parseInt(size, 10) : 25;
  };

  const updateURL = (page?: number, size?: number) => {
    const params = new URLSearchParams(searchParams);

    if (page !== undefined) {
      if (page > 1) {
        params.set("page", page.toString());
      } else {
        params.delete("page");
      }
    }

    if (size !== undefined && size !== 25) {
      params.set("pageSize", size.toString());
    } else if (size === 25) {
      params.delete("pageSize");
    }

    setSearchParams(params);
  };

  const handleSelectionChange = useCallback(
    (selectedKeys: any, selectedRows: any) => {
      setSelectedRowKeys(selectedKeys);

      setDeleteFileUpload((prevDeleteFileUpload: any) => {
        const currentPageIds = fileUploadRef.current.map((item) => item.id);
        const otherPagesSelected = prevDeleteFileUpload.filter(
          (item) => !currentPageIds.includes(item.id)
        );

        const currentPageSelected = fileUploadRef.current.filter((item) =>
          selectedKeys.includes(item.id)
        );

        return [...otherPagesSelected, ...currentPageSelected];
      });
    },
    []
  );

  const rowSelection = useMemo(
    () => ({
      type: "checkbox" as const,
      selectedRowKeys: selectedRowKeys,
      preserveSelectedRowKeys: true,
      onChange: handleSelectionChange,
      getCheckboxProps: (record: any) => ({
        disabled: record.name === "Disabled User",
        name: record.name,
      }),
    }),
    [selectedRowKeys, handleSelectionChange]
  );

  const handleChangePayableDetail = (documentId, value, type) => {
    setEditRowData((prev) => ({
      ...prev,
      [type]: value?.trim(),
    }));
  };

  console.log("editRowData", editRowData);

  const rowEditDisplay = (component, i, input) => {
    return editRowId === i?.id ? component : <div>{input}</div>;
  };

  const baseColumns = [
    {
      key: "materialSpec",
      title: t("steelBoltBOM.specification") || "材質規格-直徑-長度",
      dataIndex: "materialSpec",
      hidden: hiddenColumns.includes("materialSpec"),

      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.materialSpec?.trim()}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "materialSpec"
              )
            }
          />,
          i,
          i?.materialSpec
        );
      },
    },
    {
      key: "type",
      title: t("steelBoltBOM.partName") || "類型",
      dataIndex: "type",
      hidden: hiddenColumns.includes("type"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.type?.trim()}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "type"
              )
            }
          />,
          i,
          i?.type
        );
      },
    },
    {
      key: "quantity",
      title: t("steelBoltBOM.quantity") || "數量",
      dataIndex: "quantity",
      hidden: hiddenColumns.includes("quantity"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={Number(editRowData?.quantity)}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "quantity"
              )
            }
          />,
          i,
          i?.quantity
        );
      },
    },
    {
      key: "material",
      title: t("steelBoltBOM.material") || "材質",
      dataIndex: "material",
      hidden: hiddenColumns.includes("material"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.material?.trim()}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "material"
              )
            }
          />,
          i,
          i?.material
        );
      },
    },
    {
      key: "unitWeight",
      title: t("steelBoltBOM.weight") || "單位重(kg/m)",
      dataIndex: "unitWeight",
      hidden: hiddenColumns.includes("unitWeight"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.unitWeight}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "unitWeight"
              )
            }
          />,
          i,
          i?.unitWeight
        );
      },
    },
    {
      key: "actions",
      title: "",
      fixed: "right",
      hidden: hiddenColumns.includes("actions"),

      render: (text, i) => {
        return (
          <>
            {editRowId ? (
              <>
                <Button
                  variant="default"
                  style={{ marginRight: "15px" }}
                  onClick={() => {
                    setEditRowId(null);
                    setEditRowData([]);
                  }}
                >
                  {t("steelBoltBOM.cancel") || "取消"}
                </Button>
                <Button
                  variant="default"
                  onClick={async (element) => {
                    try {
                      console.log("id", id);
                      if (!id) return;
                      console.log("id2", id);
                      const result = await single(
                        orpcClient.steelBoltBom.update([
                          {
                            id: editRowData?.id,
                            projectId: id,
                            materialSpec: String(
                              editRowData?.materialSpec?.trim()
                            ),
                            type: editRowData?.type?.trim(),
                            quantity: Number(editRowData?.quantity),
                            material: editRowData?.material?.trim(),
                            unitWeight: Number(editRowData?.unitWeight),
                          },
                        ])
                      );

                      console.log("result", result);

                      if (result) {
                        api.success({
                          message: `Notification `,
                          description: (
                            <div>
                              {t("steelBoltBOM.editSuccess") || "編輯成功"}
                            </div>
                          ),
                          placement: "topLeft",
                          duration: 0,
                        });
                      }
                    } catch (error) {
                      api.error({
                        message: `Notification `,
                        description: (
                          <div>
                            {t("steelBoltBOM.editFailed") || "編輯失敗"}
                          </div>
                        ),
                        placement: "topLeft",
                        duration: 0,
                      });
                    }

                    fetchData(currentPage, pageSize);
                    setEditRowId(null);
                    setEditRowData([]);
                  }}
                >
                  {t("steelBoltBOM.save") || "儲存"}
                </Button>
              </>
            ) : (
              <Button
                variant="default"
                style={{ marginRight: "15px" }}
                onClick={() => {
                  setEditRowId(i?.id);
                  setEditRowData(i);
                }}
              >
                {t("steelBoltBOM.edit") || "編輯"}
              </Button>
            )}
          </>
        );
      },
    },
  ];

  const fetchData = async (page = 1, currentPageSize = pageSize) => {
    try {
      if (!id) return;

      const result = await orpcClient.steelBoltBom.list({
        filter: { projectId: id },
      });

      const totalCount = result.length;
      const startIndex = (page - 1) * currentPageSize;
      const endIndex = startIndex + currentPageSize;
      const paginatedData = result.slice(startIndex, endIndex);

      setFileUpload(paginatedData);
      setDesignBOM({
        meta: {
          pagination: {
            total: totalCount,
            page,
            pageSize: currentPageSize,
            pageCount: Math.ceil(totalCount / currentPageSize),
          },
        },
      } as any);
    } catch (error) {
      console.error("Error fetching department data:", error);
    }
  };

  const handleDeleteFileUpload = async (deleteFileUpload) => {
    if (!deleteFileUpload?.length) {
      return api.error({
        message: `Notification `,
        description: (
          <div>
            {t("steelBoltBOM.noItemsToDelete") || "請勾選需要刪除的項目"}
          </div>
        ),
        placement: "topLeft",
        duration: 0,
      });
    }
    if (!id) return;

    try {
      await allOrThrow(
        orpcClient.steelBoltBom.delete(
          deleteFileUpload.map((element) => ({ id: element?.id }))
        )
      );

      setSelectedRowKeys([]);
      setDeleteFileUpload([]);
      fetchData(currentPage, pageSize);
      api.success({
        message: `Notification `,
        description: <div>{t("steelBoltBOM.deleteSuccess") || "刪除成功"}</div>,
        placement: "topLeft",
        duration: 0,
      });
    } catch (e) {
      console.warn("error", e);
      return e;
    }
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = columnOrder.indexOf(active.id as string);
      const overIndex = columnOrder.indexOf(over?.id as string);
      const newColumnOrder = arrayMove(columnOrder, activeIndex, overIndex);
      setColumnOrder(newColumnOrder);
      localStorage.setItem(
        COLUMN_ORDER_STORAGE_KEY,
        JSON.stringify(newColumnOrder)
      );
    }
  };

  const columns = columnOrder
    .map((key) => baseColumns.find((col) => col.key === key))
    .filter((col): col is (typeof baseColumns)[number] => col !== undefined)
    .map((col, index) => {
      const newCol = { ...col };
      if (index === 0) {
        newCol.fixed = "left" as const;
      } else {
        delete newCol.fixed;
      }
      return newCol;
    });

  useEffect(() => {
    if (columnOrder.length === 0) {
      const defaultOrder = baseColumns.map((col) => col.key);
      setColumnOrder(defaultOrder);
      localStorage.setItem(
        COLUMN_ORDER_STORAGE_KEY,
        JSON.stringify(defaultOrder)
      );
    }
  }, []);

  useEffect(() => {
    const urlPage = getCurrentPageFromURL();
    const urlPageSize = getPageSizeFromURL();

    setCurrentPage(urlPage);
    setPageSize(urlPageSize);

    fetchData(urlPage, urlPageSize);
  }, [id]);

  useEffect(() => {
    const urlPage = getCurrentPageFromURL();
    const urlPageSize = getPageSizeFromURL();

    if (urlPage !== currentPage || urlPageSize !== pageSize) {
      setCurrentPage(urlPage);
      setPageSize(urlPageSize);
      fetchData(urlPage, urlPageSize);
    }
  }, [searchParams]);

  useEffect(() => {
    fileUploadRef.current = fileUpload;
  }, [fileUpload]);

  useEffect(() => {
    const handlePaginationInput = () => {
      const paginationInputs = document.querySelectorAll(
        ".ant-pagination-options .ant-select input"
      );
      paginationInputs.forEach((input) => {
        const inputElement = input as HTMLInputElement;
        inputElement.readOnly = true;
        inputElement.style.cursor = "pointer";
        inputElement.style.pointerEvents = "none";
        inputElement.style.caretColor = "transparent";
        inputElement.style.userSelect = "none";
      });

      const selectors = document.querySelectorAll(
        ".ant-pagination-options .ant-select .ant-select-selector"
      );
      selectors.forEach((selector) => {
        (selector as HTMLElement).style.cursor = "pointer";
      });
    };

    const observer = new MutationObserver(handlePaginationInput);
    observer.observe(document.body, { childList: true, subtree: true });

    const timeoutId = setTimeout(handlePaginationInput, 100);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="px-8 m-8">
      {contextHolder}
      <div
        className="flex items-center justify-between px-4"
        style={{ backgroundColor: "#ECECEC", marginBottom: "1rem" }}
      >
        <Button
          //onClick={() => navigate(`/hrMainPage/addSteelAssemblyBOM/${id}`)}
          variant="filled"
          color="red"
          onClick={() => handleDeleteFileUpload(deleteFileUpload)}
        >
          {t("steelBoltBOM.deleteSelected") || "刪除勾選的項目"}
        </Button>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            color="gray"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(`/hrMainPage/projectQuotation/${id}`)}
          >
            {t("steelAssemblyBOM.backToDesignBOM") || "回到設計BOM"}
          </Button>
          <Button
            onClick={() => navigate(`/hrMainPage/addSteelBoltBOM/${id}`)}
            variant="default"
          >
            {t("steelBoltBOM.importPart") || "匯入零件"}
          </Button>
        </div>
      </div>

      {/* Column Visibility Section */}
      <div
        className="flex items-center gap-4 px-4 py-3 mb-4"
        style={{ backgroundColor: "#F5F5F5", borderRadius: "8px" }}
      >
        <span style={{ fontWeight: "500", minWidth: "80px" }}>
          {t("steelBoltBOM.showColumns") || "顯示欄位"}:
        </span>
        <div className="flex flex-wrap gap-3">
          {baseColumns
            .filter((col) => col.key !== "actions")
            .map((col) => (
              <Checkbox
                key={col.key}
                checked={!hiddenColumns.includes(col.key)}
                onChange={(e) => {
                  const newHiddenColumns = e.target.checked
                    ? hiddenColumns.filter((k) => k !== col.key)
                    : [...hiddenColumns, col.key];
                  setHiddenColumns(newHiddenColumns);
                  localStorage.setItem(
                    COLUMN_VISIBILITY_STORAGE_KEY,
                    JSON.stringify(newHiddenColumns)
                  );
                }}
              >
                {col.title || col.key}
              </Checkbox>
            ))}
        </div>
      </div>

      <div
        className="relative"
        style={
          {
            "--ant-pagination-input-pointer-events": "none",
            "--ant-pagination-input-cursor": "pointer",
            "--ant-pagination-input-caret": "transparent",
          } as React.CSSProperties
        }
      >
        <DndContext
          sensors={sensors}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={columnOrder}
            strategy={horizontalListSortingStrategy}
          >
            <Table
              rowSelection={rowSelection}
              scroll={{ x: "max-content", y: 600 }}
              columns={columns.map((col) => ({
                ...col,
                onHeaderCell: () => ({ id: col.key }),
              }))}
              dataSource={fileUpload}
              components={{
                header: {
                  cell: DraggableHeaderCell,
                },
              }}
              pagination={{
                current: currentPage,
                pageSize,
                total: designBOM?.meta?.pagination?.total,
                // antd fires onChange for both page and size changes; handling
                // both here avoids two updateURL calls clobbering each other.
                onChange: (page, size) => {
                  const nextPage = size !== pageSize ? 1 : page;
                  setPageSize(size);
                  setCurrentPage(nextPage);
                  updateURL(nextPage, size);
                  fetchData(nextPage, size);
                },
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                showQuickJumper: false,
              }}
              rowKey="id"
              bordered
              sticky
            />
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

export default SteelBoltBOM;
